import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { User, Clock, Edit2, Save, X, Receipt, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const MyProfileTab = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState({});
  const [clockHistory, setClockHistory] = useState([]);
  const [employeeStubs, setEmployeeStubs] = useState([]);
  const [loadingStubs, setLoadingStubs] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [activeSection, setActiveSection] = useState('info');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [profileRes, clockRes] = await Promise.all([
        axios.get(`/hr/employees/${user?.user_id}/profile`),
        axios.get('/clock/history')
      ]);
      setProfile(profileRes.data || {});
      setClockHistory((clockRes.data || []).slice(0, 30));
      loadStubs();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadStubs = async () => {
    setLoadingStubs(true);
    try {
      const res = await axios.get(`/pay-stubs/employee/${user?.user_id}`);
      setEmployeeStubs(res.data || []);
    } catch (error) {
      setEmployeeStubs([]);
    } finally {
      setLoadingStubs(false);
    }
  };

  const downloadStubPdf = async (stubId) => {
    try {
      const res = await axios.get(`/pay-stubs/${stubId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `talonario_${stubId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Talonario descargado');
    } catch (error) {
      toast.error('Error al descargar el talonario');
    }
  };

  const handleProfileChange = (field, value) => setProfile(prev => ({ ...prev, [field]: value }));

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`/hr/employees/${user?.user_id}/profile/self`, profile);
      toast.success('Perfil actualizado correctamente');
      setEditingProfile(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingProfile(false);
    }
  };

  const formatClockTime = (isoStr) => {
    if (!isoStr) return '--:--';
    return new Date(isoStr).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const groupedHistory = clockHistory.reduce((groups, entry) => {
    const date = entry.date;
    if (!groups[date]) groups[date] = [];
    groups[date].push(entry);
    return groups;
  }, {});

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="my-profile-panel">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
          <User className="w-8 h-8 text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">{user?.username || 'Mi Perfil'}</h2>
          <p className="text-slate-500 capitalize">{user?.role || ''}</p>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="border-b">
        <div className="flex gap-6">
          {[
            { id: 'info', label: 'Información', icon: User },
            { id: 'hours', label: 'Mis Horas', icon: Clock },
            { id: 'stubs', label: 'Talonarios', icon: Receipt }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveSection(tab.id)}
              className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeSection === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Info Tab */}
      {activeSection === 'info' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Mi Información</CardTitle>
              {!editingProfile ? (
                <Button size="sm" variant="outline" onClick={() => setEditingProfile(true)} data-testid="edit-profile-btn">
                  <Edit2 className="w-4 h-4 mr-1" /> Editar
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditingProfile(false)}>
                    <X className="w-4 h-4 mr-1" /> Cancelar
                  </Button>
                  <Button size="sm" onClick={saveProfile} disabled={savingProfile} className="bg-blue-600 hover:bg-blue-700" data-testid="save-profile-btn">
                    <Save className="w-4 h-4 mr-1" /> {savingProfile ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 border-b pb-2">Datos Personales</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Teléfono</Label>
                  {editingProfile ? (
                    <Input value={profile.phone || ''} onChange={(e) => handleProfileChange('phone', e.target.value)} placeholder="(xxx) xxx-xxxx" />
                  ) : (
                    <p className="text-sm font-medium">{profile.phone || 'No especificado'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Fecha de Nacimiento</Label>
                  {editingProfile ? (
                    <Input type="date" value={profile.date_of_birth || ''} onChange={(e) => handleProfileChange('date_of_birth', e.target.value)} />
                  ) : (
                    <p className="text-sm font-medium">{profile.date_of_birth || 'No especificado'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Género</Label>
                  {editingProfile ? (
                    <Select value={profile.gender || 'none'} onValueChange={(v) => handleProfileChange('gender', v === 'none' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No especificado</SelectItem>
                        <SelectItem value="male">Masculino</SelectItem>
                        <SelectItem value="female">Femenino</SelectItem>
                        <SelectItem value="other">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm font-medium">{profile.gender === 'male' ? 'Masculino' : profile.gender === 'female' ? 'Femenino' : profile.gender || 'No especificado'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Estado Civil</Label>
                  {editingProfile ? (
                    <Select value={profile.marital_status || 'none'} onValueChange={(v) => handleProfileChange('marital_status', v === 'none' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No especificado</SelectItem>
                        <SelectItem value="single">Soltero/a</SelectItem>
                        <SelectItem value="married">Casado/a</SelectItem>
                        <SelectItem value="divorced">Divorciado/a</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm font-medium">
                      {profile.marital_status === 'single' ? 'Soltero/a' : profile.marital_status === 'married' ? 'Casado/a' : profile.marital_status === 'divorced' ? 'Divorciado/a' : 'No especificado'}
                    </p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-slate-500">Dirección</Label>
                  {editingProfile ? (
                    <Input value={profile.address || ''} onChange={(e) => handleProfileChange('address', e.target.value)} />
                  ) : (
                    <p className="text-sm font-medium">{profile.address || 'No especificado'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Ciudad</Label>
                  {editingProfile ? (
                    <Input value={profile.city || ''} onChange={(e) => handleProfileChange('city', e.target.value)} />
                  ) : (
                    <p className="text-sm font-medium">{profile.city || 'No especificado'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Código Postal</Label>
                  {editingProfile ? (
                    <Input value={profile.zipcode || ''} onChange={(e) => handleProfileChange('zipcode', e.target.value)} />
                  ) : (
                    <p className="text-sm font-medium">{profile.zipcode || 'No especificado'}</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 border-b pb-2">Contacto de Emergencia</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Nombre</Label>
                  {editingProfile ? (
                    <Input value={profile.emergency_contact_name || ''} onChange={(e) => handleProfileChange('emergency_contact_name', e.target.value)} />
                  ) : (
                    <p className="text-sm font-medium">{profile.emergency_contact_name || 'No especificado'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Teléfono</Label>
                  {editingProfile ? (
                    <Input value={profile.emergency_contact_phone || ''} onChange={(e) => handleProfileChange('emergency_contact_phone', e.target.value)} />
                  ) : (
                    <p className="text-sm font-medium">{profile.emergency_contact_phone || 'No especificado'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Relación</Label>
                  {editingProfile ? (
                    <Input value={profile.emergency_contact_relationship || ''} onChange={(e) => handleProfileChange('emergency_contact_relationship', e.target.value)} />
                  ) : (
                    <p className="text-sm font-medium">{profile.emergency_contact_relationship || 'No especificado'}</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hours Tab */}
      {activeSection === 'hours' && (
        <div>
          {Object.keys(groupedHistory).length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
              No hay registros de horas
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {Object.entries(groupedHistory).map(([date, entries]) => {
                const totalHours = entries.reduce((sum, e) => sum + (e.hours_worked || 0), 0);
                return (
                  <Card key={date}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-semibold text-slate-800">
                          {new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </div>
                        <Badge className="bg-blue-100 text-blue-700">{totalHours.toFixed(2)}h total</Badge>
                      </div>
                      <div className="space-y-2 ml-2 border-l-2 border-slate-200 pl-3">
                        {entries.map((entry, idx) => (
                          <div key={entry.clock_id || idx} className="flex items-center justify-between text-sm py-1">
                            <div className="flex items-center gap-3">
                              <span className="font-mono font-medium text-slate-800">{formatClockTime(entry.clock_in)}</span>
                              <span className="text-slate-400">-</span>
                              <span className={`font-mono font-medium ${entry.clock_out ? 'text-slate-800' : 'text-green-600'}`}>
                                {entry.clock_out ? formatClockTime(entry.clock_out) : 'Activo'}
                              </span>
                            </div>
                            <span className={`text-sm font-medium ${entry.status === 'active' ? 'text-green-600' : 'text-blue-600'}`}>
                              {entry.status === 'active' ? 'En curso' : `${(entry.hours_worked || 0).toFixed(2)}h`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Talonarios Tab */}
      {activeSection === 'stubs' && (
        <div data-testid="my-stubs-section">
          {loadingStubs ? (
            <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : employeeStubs.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-slate-500">
                <Receipt className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No hay talonarios disponibles</p>
                <p className="text-xs text-slate-400 mt-1">Los talonarios se generan al procesar la nómina</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Receipt className="w-4 h-4" /> Mis Talonarios de Pago
                </h3>
                <Badge className="bg-blue-50 text-blue-700 border-blue-200">{employeeStubs.length} talonario(s)</Badge>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden" data-testid="my-stubs-table">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600">Periodo</th>
                      <th className="text-right p-3 font-medium text-slate-600">Horas</th>
                      <th className="text-right p-3 font-medium text-slate-600">Bruto</th>
                      <th className="text-right p-3 font-medium text-slate-600">Hacienda</th>
                      <th className="text-right p-3 font-medium text-slate-600">Seg. Social</th>
                      <th className="text-right p-3 font-medium text-slate-600">Medicare</th>
                      <th className="text-right p-3 font-medium text-slate-600">Deducciones</th>
                      <th className="text-right p-3 font-medium text-slate-600">Neto</th>
                      <th className="text-left p-3 font-medium text-slate-600">Método</th>
                      <th className="text-left p-3 font-medium text-slate-600">Fecha</th>
                      <th className="text-center p-3 font-medium text-slate-600">Descargar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeStubs.map((stub, idx) => (
                      <tr key={stub.id || idx} className="border-b hover:bg-slate-50" data-testid={`my-stub-row-${idx}`}>
                        <td className="p-3 font-medium text-xs">{stub.period_start} - {stub.period_end}</td>
                        <td className="p-3 text-right font-mono text-xs">{(stub.hours_worked || 0).toFixed(1)}h</td>
                        <td className="p-3 text-right font-mono text-xs">${(stub.gross_pay || 0).toFixed(2)}</td>
                        <td className="p-3 text-right font-mono text-xs text-orange-600">${(stub.hacienda || 0).toFixed(2)}</td>
                        <td className="p-3 text-right font-mono text-xs text-blue-600">${(stub.ss || 0).toFixed(2)}</td>
                        <td className="p-3 text-right font-mono text-xs text-purple-600">${(stub.medicare || 0).toFixed(2)}</td>
                        <td className="p-3 text-right font-mono text-xs text-red-600">-${(stub.total_deductions || 0).toFixed(2)}</td>
                        <td className="p-3 text-right font-mono text-xs font-bold text-green-600">${(stub.net_pay || 0).toFixed(2)}</td>
                        <td className="p-3 text-xs capitalize">{stub.payment_method || 'N/A'}</td>
                        <td className="p-3 text-xs text-slate-500">{stub.created_at ? new Date(stub.created_at).toLocaleDateString('es-ES') : 'N/A'}</td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => downloadStubPdf(stub.id)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                            title="Descargar PDF"
                            data-testid={`download-stub-${idx}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 font-bold text-xs">
                    <tr>
                      <td className="p-3">TOTALES ({employeeStubs.length})</td>
                      <td className="p-3 text-right font-mono">{employeeStubs.reduce((a, s) => a + (s.hours_worked || 0), 0).toFixed(1)}h</td>
                      <td className="p-3 text-right font-mono">${employeeStubs.reduce((a, s) => a + (s.gross_pay || 0), 0).toFixed(2)}</td>
                      <td className="p-3 text-right font-mono text-orange-600">${employeeStubs.reduce((a, s) => a + (s.hacienda || 0), 0).toFixed(2)}</td>
                      <td className="p-3 text-right font-mono text-blue-600">${employeeStubs.reduce((a, s) => a + (s.ss || 0), 0).toFixed(2)}</td>
                      <td className="p-3 text-right font-mono text-purple-600">${employeeStubs.reduce((a, s) => a + (s.medicare || 0), 0).toFixed(2)}</td>
                      <td className="p-3 text-right font-mono text-red-600">-${employeeStubs.reduce((a, s) => a + (s.total_deductions || 0), 0).toFixed(2)}</td>
                      <td className="p-3 text-right font-mono text-green-600">${employeeStubs.reduce((a, s) => a + (s.net_pay || 0), 0).toFixed(2)}</td>
                      <td colSpan="3" className="p-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyProfileTab;
